/**
 * The browser's side of landing pages (migration 00034). Rows are read and
 * written through /api/db with the signed-in person's row security: who can
 * open a page follows its visibility, and only the owner or an administrator
 * can change it. Home and sidebar placement are kept by the database for
 * administrators only, whatever the browser sends.
 */
import { supabase } from '@/lib/supabase'
import type { Visibility } from '@/services/reports/reportDefinition'
import { normalizeLayout, type LandingLayout, type LandingPage } from './landingTypes'

function explain(message: string): string {
  if (/wff_landing|schema cache|does not exist/i.test(message)) {
    return 'Landing pages are not installed in this environment yet. Ask an administrator to apply database migration 00034.'
  }
  if (/wff_landing_pages_slug_key|duplicate key/i.test(message)) return 'Another page already uses that web address. Choose a different one.'
  if (/row-level security|permission denied/i.test(message)) return 'You cannot change this page. Only its owner or an administrator can.'
  return message
}

const strings = (v: unknown): string[] => (Array.isArray(v) ? v.map(String) : [])

function toPage(r: Record<string, unknown>): LandingPage {
  return {
    id: String(r.id),
    name: String(r.name ?? 'Page'),
    slug: String(r.slug ?? ''),
    description: (r.description as string) ?? null,
    icon: (r.icon as string) ?? null,
    layout: normalizeLayout(r.layout),
    draft: r.draft ? normalizeLayout(r.draft) : null,
    publishedAt: (r.published_at as string) ?? null,
    publishedBy: (r.published_by as string) ?? null,
    visibility: (r.visibility as Visibility) ?? 'private',
    sharePrincipals: strings(r.share_principals),
    homeFor: strings(r.home_for),
    homePriority: Number(r.home_priority ?? 100),
    showInNav: r.show_in_nav === true,
    navOrder: Number(r.nav_order ?? 100),
    systemKey: (r.system_key as string) ?? null,
    ownerEmail: (r.owner_email as string) ?? null,
    ownerName: (r.owner_name as string) ?? null,
    updatedAt: String(r.updated_at ?? ''),
  }
}

export async function listLandingPages(): Promise<LandingPage[]> {
  const { data, error } = await supabase.from('wff_landing_pages').select('*').order('name')
  if (error) throw new Error(explain(error.message))
  return ((data ?? []) as Record<string, unknown>[]).map(toPage)
}

export async function getLandingPageBySlug(slug: string): Promise<LandingPage | null> {
  const { data, error } = await supabase.from('wff_landing_pages').select('*').eq('slug', slug).maybeSingle()
  if (error) throw new Error(explain(error.message))
  return data ? toPage(data as Record<string, unknown>) : null
}

export async function getLandingPage(id: string): Promise<LandingPage | null> {
  const { data, error } = await supabase.from('wff_landing_pages').select('*').eq('id', id).maybeSingle()
  if (error) throw new Error(explain(error.message))
  return data ? toPage(data as Record<string, unknown>) : null
}

/** The signed-in person's home page slug, or null when none is set for them. */
export async function getLandingHome(): Promise<string | null> {
  const { data, error } = await supabase.rpc('wff_landing_home')
  if (error) throw new Error(explain(error.message))
  return (data as string | null) ?? null
}

export interface LandingSettings {
  name: string
  slug: string
  description: string | null
  icon: string | null
  visibility: Visibility
  sharePrincipals: string[]
  homeFor: string[]
  homePriority: number
  showInNav: boolean
  navOrder: number
}

function settingsRow(s: LandingSettings) {
  return {
    name: s.name.trim(),
    slug: s.slug,
    description: s.description?.trim() || null,
    icon: s.icon || null,
    visibility: s.visibility,
    share_principals: s.visibility === 'shared' ? s.sharePrincipals : [],
    home_for: s.homeFor,
    home_priority: s.homePriority,
    show_in_nav: s.showInNav,
    nav_order: s.navOrder,
  }
}

export async function createLandingPage(settings: LandingSettings, layout: LandingLayout): Promise<LandingPage> {
  // The slug was chosen against the pages this person can see; another
  // person's private page may already hold it, so retry with a suffix.
  for (let attempt = 0; ; attempt++) {
    const slug = attempt === 0 ? settings.slug : `${settings.slug.slice(0, 70)}-${Math.random().toString(36).slice(2, 6)}`
    const { data, error } = await supabase.from('wff_landing_pages')
      .insert({ ...settingsRow({ ...settings, slug }), layout, draft: layout })
      .select('*').single()
    if (!error) return toPage(data as Record<string, unknown>)
    if (attempt >= 2 || !/wff_landing_pages_slug_key|duplicate key/i.test(error.message)) throw new Error(explain(error.message))
  }
}

export async function saveLandingSettings(id: string, settings: LandingSettings): Promise<LandingPage> {
  const { data, error } = await supabase.from('wff_landing_pages').update(settingsRow(settings)).eq('id', id).select('*').single()
  if (error) throw new Error(explain(error.message))
  return toPage(data as Record<string, unknown>)
}

/** Save unpublished edits. Viewers keep seeing the published layout. */
export async function saveLandingDraft(id: string, draft: LandingLayout): Promise<LandingPage> {
  const { data, error } = await supabase.from('wff_landing_pages').update({ draft }).eq('id', id).select('*').single()
  if (error) throw new Error(explain(error.message))
  return toPage(data as Record<string, unknown>)
}

/** Publish: the draft becomes what everyone sees. */
export async function publishLandingPage(id: string, layout: LandingLayout): Promise<LandingPage> {
  const { data, error } = await supabase.from('wff_landing_pages')
    .update({ layout, draft: null, published_at: new Date().toISOString() })
    .eq('id', id).select('*').single()
  if (error) throw new Error(explain(error.message))
  return toPage(data as Record<string, unknown>)
}

/** Throw the draft away and go back to the published layout. */
export async function discardLandingDraft(id: string): Promise<LandingPage> {
  const { data, error } = await supabase.from('wff_landing_pages').update({ draft: null }).eq('id', id).select('*').single()
  if (error) throw new Error(explain(error.message))
  return toPage(data as Record<string, unknown>)
}

export async function unpublishLandingPage(id: string): Promise<LandingPage> {
  const page = await getLandingPage(id)
  const { data, error } = await supabase.from('wff_landing_pages')
    .update({ published_at: null, draft: page?.draft ?? page?.layout ?? null })
    .eq('id', id).select('*').single()
  if (error) throw new Error(explain(error.message))
  return toPage(data as Record<string, unknown>)
}

export async function deleteLandingPage(id: string): Promise<void> {
  const { error } = await supabase.from('wff_landing_pages').delete().eq('id', id)
  if (error) throw new Error(explain(error.message))
}
